import {
  ResourceSettingsActionType,
  useResourceSettingsState
} from '@/hooks/useResourceSettings';
import { api } from '@/lib/api/api';
import { IResource } from '@gettactic/api';
import { useAreas } from '@gettactic/hooks';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useIsDesignSystem2 } from '@webV2/helpers/useIsDesignSystem2';

export function useEditResourceRouter(
  officeId: string,
  entities: IResource['type'][]
) {
  const [query, setQuery] = useState<string>('');
  const [bulk, setBulk] = useState<string[]>([]);
  const [floor, setFloor] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<IResource[]>([]);
  const [isSideDrawerOpen, setIsSideDrawerOpen] = useState<boolean>(false);
  const resourceContext = useResourceSettingsState();
  const router = useRouter();
  const { areas } = useAreas(officeId);
  const isV2 = useIsDesignSystem2();
  const resourceListQuery = useQuery(
    [
      'organizations.resourcesAll',
      officeId,
      entities,
      floor,
      null,
      undefined,
      undefined,
      query,
      undefined,
      undefined,
      !!floor
    ],
    async () => {
      if (isV2) {
        const res = await api.client.organizations.getAllResources({
          officeId: officeId,
          types: entities,
          parentId: floor ?? undefined,
          filter: null,
          query: query,
          hierarchy: !!floor
        });
        return res.result;
      }

      const res = await api.client.organizations.resourcesAll(
        officeId,
        entities,
        floor,
        null,
        undefined,
        undefined,
        query,
        undefined,
        undefined,
        !!floor
      );
      return res.result;
    },
    {
      enabled: entities.length > 0
    }
  );
  const openResourceId = useCallback(
    (resId: string) => {
      if (!resourceListQuery.data) return;
      const resource = resourceListQuery.data.byId[resId];
      if (resource) {
        const { edit, ...rest } = router.query;
        // replace the edit param, so it's not opened multiple times
        if (router.pathname !== '/map') {
          router.replace({
            query: { ...rest },
            hash: router.asPath.split('#')[1]
          });
        }
        resourceContext.dispatch({
          type: ResourceSettingsActionType.SetSelectedResource,
          payload: resource
        });
        setIsSideDrawerOpen(true);
      }
    },
    [resourceListQuery.data, router, resourceContext]
  );
  useEffect(() => {
    if (
      !router.isReady ||
      !resourceListQuery.data ||
      !router.query?.edit?.toString()
    ) {
      return;
    }
    openResourceId(router.query?.edit?.toString());
  }, [router.isReady, router.query, resourceListQuery.data, openResourceId]);

  const resourceList = resourceListQuery.data?.elements ?? [];
  const selectedAll = resourceList.length
    ? resourceList.length === bulk.length
    : false;
  const visibleSelected = resourceList.filter((x) => bulk.indexOf(x.id) > -1);

  return {
    resourceContext,
    resourceList,
    deleteModal,
    setDeleteModal,
    isSideDrawerOpen,
    setIsSideDrawerOpen,
    areas,
    openResourceId,
    query,
    setQuery,
    floor,
    setFloor,
    bulk,
    setBulk,
    selectedAll,
    visibleSelected
  };
}
